import recaptcha from '@components/App/RecaptchaWidget/recaptcha'
import { LoaderActions, selectIsLoading } from '@store/loaders'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { normalize } from '@/helpers'
import { useProjectAccess } from '@/hooks'
import { useGetDataForExport } from '@/hooks/exportDropdown'
import { EXPORT_PERMISSION } from '@/permissions/project/definitions'
import { normalizeExportData } from './helper/normalizeExportData'
import { useEstimatePayload } from './useEstimatePayload'

const EXPORT_EXCEL_URL: string | undefined = import.meta.env
  .VITE_EXPORT_PDF_API_URL

export const useExportAsExcel = () => {
  const dispatch = useDispatch()

  const projectTitle = useSelector(migrationProjectSelector.getProjectTitle)
  const isFetching = useSelector(selectIsLoading('export_xls'))

  const { getExportData } = useGetDataForExport()
  const getEstimatePayload = useEstimatePayload()

  const { hasAccess } = useProjectAccess(EXPORT_PERMISSION.FILE_FORMATS)

  return async () => {
    if (isFetching || !hasAccess || !EXPORT_EXCEL_URL) {
      return
    }

    sendGtmEvent(GtmTriggerName.EXPORT_EXCEL, 'Clicked on XLS button')

    const content = normalizeExportData(getExportData())

    const recaptchaResponseKey = await recaptcha.execute({
      action: 'useExportAsExcel',
    })

    try {
      dispatch(LoaderActions.set('export_xls'))

      const response = await axios.post(
        `${EXPORT_EXCEL_URL}/export-excel?responseKey=${recaptchaResponseKey}`,
        { content, estimateData: getEstimatePayload() },
        { responseType: 'blob' }
      )

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })

      const downloadUrl = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `${normalize(projectTitle)}.xlsx`
      link.click()

      URL.revokeObjectURL(downloadUrl)
    } finally {
      dispatch(LoaderActions.unset('export_xls'))
    }
  }
}
